// Labelled inputs, so a form is written as fields rather than as markup.
//
// The `<Field label><input className={cx("mono", inputClass)} inputMode="decimal"
// …/></Field>` triple appeared dozens of times, and the numeric ones disagreed
// about whether to set `inputMode` at all — which decides whether a phone shows
// a number pad.
//
// Everything here is a thin wrapper over components/ui's Field + inputClass. A
// form that needs a control these don't cover uses `<Field>` directly.

import type { ChangeEvent, KeyboardEvent, ReactNode } from "react";
import { cx, Field, inputClass } from "@/platform/ui/primitives/index.js";

interface Base {
  label: string;
  autoFocus?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function TextField({
  label,
  value,
  onChange,
  onEnter,
  ...rest
}: Base & { value: string; onChange: (v: string) => void; onEnter?: () => void }) {
  return (
    <Field label={label}>
      <input
        className={inputClass}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        onKeyDown={
          onEnter
            ? (e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onEnter();
                }
              }
            : undefined
        }
        {...rest}
      />
    </Field>
  );
}

// A description field should be tall enough to show existing content without
// immediately scrolling, but not grow without bound — clamp to a reasonable
// range and estimate wrapped rows at ~60 chars/line (this repo has no
// textarea-autosize dependency, and rows-by-scrollHeight needs a DOM ref/
// layout pass; this stays a pure function of the value instead).
function autoRows(value: string, min = 2, max = 8): number {
  if (!value) return min;
  const lines = value
    .split("\n")
    .reduce((total, line) => total + Math.max(1, Math.ceil(line.length / 60)), 0);
  return Math.min(max, Math.max(min, lines));
}

export function TextArea({
  label,
  value,
  onChange,
  onEnter,
  rows,
  ...rest
}: Base & { value: string; onChange: (v: string) => void; onEnter?: () => void; rows?: number }) {
  return (
    <Field label={label}>
      <textarea
        className={inputClass}
        rows={rows ?? autoRows(value)}
        value={value}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
        onKeyDown={
          onEnter
            ? (e: KeyboardEvent<HTMLTextAreaElement>) => {
                // Shift+Enter keeps the newline; plain Enter submits.
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onEnter();
                }
              }
            : undefined
        }
        {...rest}
      />
    </Field>
  );
}

/**
 * A numeric input kept as a *string*.
 *
 * Numbers are parsed on submit, not on keystroke: coercing as the user types
 * makes "0.", "-", and a cleared field unrepresentable, and every form here
 * already learned that the hard way.
 */
export function NumberField({
  label,
  value,
  onChange,
  mono = true,
  ...rest
}: Base & { value: string; onChange: (v: string) => void; mono?: boolean }) {
  return (
    <Field label={label}>
      <input
        className={mono ? cx("mono", inputClass) : inputClass}
        inputMode="decimal"
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        {...rest}
      />
    </Field>
  );
}

export function DateField({
  label,
  value,
  onChange,
  max,
  ...rest
}: Base & { value: string; onChange: (v: string) => void; max?: string }) {
  return (
    <Field label={label}>
      <input
        type="date"
        className={cx("mono", inputClass)}
        value={value}
        max={max}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        {...rest}
      />
    </Field>
  );
}

interface SelectOption<T extends string> {
  value: T;
  label: ReactNode;
}

export function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  ...rest
}: Base & { value: T; onChange: (v: T) => void; options: SelectOption<T>[] }) {
  return (
    <Field label={label}>
      <select
        className={inputClass}
        value={value}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value as T)}
        {...rest}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}
